import { openModal } from "action/modal/openModal";
import { ICompactProduct } from "api/interfaces/get-page-details/common/CompactProduct.type";
import { ProductImage } from "api/interfaces/product-api.type";
import classnames from "classnames";
import PAImage from "component/design-system/images/PAImage";
import ThumbnailVideo from "component/product/V2/image-section/thumbnail/ThumbnailVideo";
import useScreenSize from "hoc/useScreenSize";
import Image from "next/legacy/image";
import { useState } from "react";
import { useDispatch } from "react-redux";
import HideIf from "util/HideIf";
import { getProductImage } from "util/helper/getProductImage";
import { getRelativeImagePath } from "util/helper/getRelativeImagePath";
import { getThumbImage } from "util/helper/getThumbImage";
import { getProductHeading } from "util/product/get-product-heading";
import styles from "./apparel-component.module.scss";

interface IProps {
  images: string[];
  title: string;
  manufacturerImage: string;
  productImagesV2: ProductImage[];
  product: ICompactProduct;
  videos: {
    title: string;
    youtubeId: string;
    thumbnailUrl: string;
  }[];
}

const ApparelImageSection = ({
  images, title, manufacturerImage, productImagesV2, product, videos = [],
}: IProps) => {
  const thumbnailSize = 120;
  const [isMobile, isTablet, isDesktop] = useScreenSize();
  const dispatch = useDispatch();

  let imagePreload = "";

  if (isMobile) {
    imagePreload = "m";
  } else if (isTablet) {
    imagePreload = "t";
  } else if (isDesktop) {
    imagePreload = "ds";
  }
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const selectedImageUrl = images[selectedImageIndex];
  let updatedMedia: any[] = images.map((image) => (
    { type: "image", image }
  ));
  if (videos.length > 0) {
    updatedMedia = [...updatedMedia.slice(0, 3), { type: "video", videos }, ...updatedMedia.slice(3)];
  }

  function videoIndex() {
    return updatedMedia.findIndex((media) => media.type === "video");
  }

  function getImageIndex(selectedImage: number) {
    let updatedIndexOfImage = selectedImage;
    const videoIdx = videoIndex();
    if (videoIdx !== -1 && selectedImage > videoIdx) {
      updatedIndexOfImage -= 1;
    }
    return updatedIndexOfImage;
  }

  function handleImageClick(selectedImage: number, isImageClick = true) {
    dispatch(openModal("thumbnailVideo", {
      productImagesV2,
      getThumbImagePath: getThumbImage,
      imageTitle: getProductHeading(product),
      product,
      productLineVideos: [...videos],
      isImageClick,
      defaultSelectedImage: getImageIndex(selectedImage),
      isMerchProduct: true,
      isMobile,
    }));
  }

  return (
    <div
      className={styles.apparel_images_container}
    >
      <div className={styles.apparel_manufacturer}>
        <Image
          src={getRelativeImagePath(manufacturerImage)}
          width={155}
          height={42}
          alt="Parts Avatar Logo"
        />
      </div>
      <HideIf show={selectedImageUrl}>
        <div className={styles.apparel_media_frame}>
          <div
            onClick={() => handleImageClick(selectedImageIndex)}
          >
            <PAImage
              src={getProductImage(selectedImageUrl, false, imagePreload)}
              alt={title}
              className={styles.apparel_main_image}
            />
          </div>
          <HideIf show={images.length > 1}>
            <div className={`${styles.apparel_thumbnail_row} align-items-center`}>
              {updatedMedia.filter(Boolean).map((media, idx) => {
                if (media.type === "video") {
                  return (
                    <ThumbnailVideo
                      key="video-thumbnail"
                      productImagesV2={productImagesV2}
                      getThumbImagePath={getThumbImage}
                      imageTitle={getProductHeading(product)}
                      product={product}
                      productLineVideos={videos}
                      thumbnailSize={thumbnailSize}
                    />
                  );
                }
                const { image } = media;
                const imageIndex = getImageIndex(idx);
                return (
                  <div
                    key={image}
                    className={classnames(styles.apparel_thumbnail, { [styles.apparel_thumbnail_selected]: selectedImageIndex === imageIndex })}
                    onClick={() => setSelectedImageIndex(imageIndex)}
                  >
                    <PAImage
                      src={getProductImage(image, false, imagePreload)}
                      alt={title}
                      width={thumbnailSize}
                      height={thumbnailSize}
                      className={styles.apparel_thumbnail_image}
                    />
                  </div>
                );
              })}
            </div>
          </HideIf>
        </div>
      </HideIf>
    </div>
  );
};

export default ApparelImageSection;
